import { useEffect, useCallback, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { Plus, Eye } from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { api } from "../services/api";
import Button from "./Button";
import SortableBlock from "./SortableBlock";
import TranslationBanner from "./TranslationBanner";
import PostTranslationBanner from "./PostTranslationBanner";
import ContentMetaSidebar from "./ContentMetaSidebar";
import { useBlockBuilder } from "../hooks/useBlockBuilder";
import usePageBuilderStore from "../stores/pageBuilderStore";
import { useLanguageStore } from "../stores/languageStore";
import { useContentMetaStore } from "../stores/contentMetaStore";
import { getEntityConfig } from "../config/entityConfigs";

export default function ContentBuilder({ entityType }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isNew = id === "new";

  const config = getEntityConfig(entityType);
  const useStore = useContentMetaStore(entityType);

  const meta = useStore((state) => state.meta);
  const resetMeta = useStore((state) => state.resetMeta);
  const setSaveHandler = useStore((state) => state.setSaveHandler);
  const setSaveDraftHandler = useStore((state) => state.setSaveDraftHandler);
  const setPublishHandler = useStore((state) => state.setPublishHandler);
  const setDiscardDraftHandler = useStore(
    (state) => state.setDiscardDraftHandler
  );
  const setIsSaving = useStore((state) => state.setIsSaving);
  const setIsPublishing = useStore((state) => state.setIsPublishing);
  const setIsDiscarding = useStore((state) => state.setIsDiscarding);

  const [isDiscardingState, setIsDiscardingState] = useState(false);

  const {
    currentLanguageCode: locale,
    _hasHydrated,
    initializeLanguage,
  } = useLanguageStore();

  const setViewMode = usePageBuilderStore((state) => state.setViewMode);
  const setDraftStates = usePageBuilderStore((state) => state.setDraftStates);
  const clearLocalBlocks = usePageBuilderStore(
    (state) => state.clearLocalBlocks
  );
  const setIsViewModeChanging = usePageBuilderStore(
    (state) => state.setIsViewModeChanging
  );
  const setIsLocaleChanging = usePageBuilderStore(
    (state) => state.setIsLocaleChanging
  );

  const {
    blocks,
    blocksLoading,
    selectedBlockId,
    hasUnsavedChanges,
    hasDraftStates,
    viewMode,
    draftStates,
    localBlocks,
    setSelectedBlockId,
    createBlock,
    updateBlock,
    deleteBlock,
    handleFieldChange,
    handleSaveDrafts,
    handlePublish,
    handleDiscard,
    isSaving,
    isPublishing,
  } = useBlockBuilder({
    entityType: config.singular,
    entityId: id,
    isNew,
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const { data: entityRes } = useQuery({
    queryKey: [config.api.queryKeys.single, id, locale],
    queryFn: () =>
      api
        .get(config.api.endpoints.single.replace(":id", id), {
          params: { locale },
        })
        .then((r) => r.data),
    enabled: !!id && !isNew,
  });

  const { data: languagesRes } = useQuery({
    queryKey: ["languages"],
    queryFn: () => api.get("/languages").then((r) => r.data),
  });

  const entity = entityRes?.data || null;
  const languages = Array.isArray(languagesRes?.data)
    ? languagesRes.data
    : Array.isArray(languagesRes)
    ? languagesRes
    : [];

  const hasTranslation = entity?.has_translation_for_locale ?? true;
  const currentLanguage = languages.find((lang) => lang.code === locale);
  const languageName =
    currentLanguage?.name || locale?.toUpperCase() || "Unknown";

  useEffect(() => {
    if (_hasHydrated && languages.length > 0 && !locale) {
      initializeLanguage(languages);
    }
  }, [languages, locale, initializeLanguage, _hasHydrated]);

  useEffect(() => {
    return () => {
      if (isNew) {
        resetMeta();
        clearLocalBlocks();
      }
    };
  }, [isNew, resetMeta, clearLocalBlocks]);

  const createMutation = useMutation({
    mutationFn: (data) => api.post(config.api.endpoints.create, data),
    onSuccess: async (response) => {
      const newEntityId = response.data.data.id;

      if (localBlocks.length > 0) {
        const blockPromises = localBlocks.map((block) => {
          const localDraft = draftStates[block.id] || {};
          const mergedContent = { ...block.content, ...localDraft };
          const blockData = {
            [config.blockField]: newEntityId,
            block_type_id: block.block_type_id,
            name: block.name,
            content: mergedContent,
            settings: block.settings,
            sort_order: block.sort_order,
            locale: locale,
          };
          return api.post("/blocks", blockData);
        });

        try {
          await Promise.all(blockPromises);
        } catch (error) {
          alert(
            `${config.singularLabel} created but some blocks failed to save. Please try adding them again.`
          );
        }
      }

      queryClient.invalidateQueries([config.api.queryKeys.list, locale]);
      clearLocalBlocks();
      navigate(`${config.routes.builder.replace(":id", newEntityId)}`, {
        replace: true,
      });
    },
    onError: (error) => {
      const message =
        error.response?.data?.message ||
        error.message ||
        `Failed to create ${config.singular}`;
      alert(`Error: ${message}`);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data) =>
      api.put(config.api.endpoints.update.replace(":id", id), data),
    onSuccess: () => {
      queryClient.invalidateQueries([config.api.queryKeys.single, id, locale]);
      queryClient.invalidateQueries([config.api.queryKeys.list, locale]);
    },
    onError: (error) => {
      const message =
        error.response?.data?.message ||
        error.message ||
        `Failed to update ${config.singular}`;
      alert(`Error: ${message}`);
    },
  });

  const publishEntityMutation = useMutation({
    mutationFn: () => api.post(config.api.endpoints.publish.replace(":id", id)),
    onSuccess: () => {
      queryClient.invalidateQueries([config.api.queryKeys.single, id, locale]);
      queryClient.invalidateQueries([config.api.queryKeys.list, locale]);
    },
    onError: (error) => {},
  });

  const handleSaveEntity = () => {
    const missingFields = config.fields.required.filter(
      (field) => !meta[field]
    );
    if (missingFields.length > 0) {
      const fieldLabels = missingFields.map((field) => {
        const fieldConfig = config.fields.sidebar.find((f) => f.name === field);
        return fieldConfig?.label || field;
      });
      alert(`Please fill in required fields: ${fieldLabels.join(", ")}`);
      return;
    }

    const payload = {
      ...meta,
      locale: locale,
      translations: [
        {
          locale: locale,
          title: meta.title,
          slug: meta.slug,
          excerpt: meta.excerpt,
          content: "",
          meta_description: meta.meta_description,
        },
      ],
      categories: meta.categories.map((cat) => cat.id),
    };

    if (isNew) {
      createMutation.mutate(payload);
    } else {
      updateMutation.mutate(payload);
    }
  };

  const handleSaveAll = useCallback(async () => {
    if (isNew) {
      await handleSaveEntity();
    } else {
      await updateMutation.mutateAsync({
        ...meta,
        locale: locale,
        translations: [
          {
            locale: locale,
            title: meta.title,
            slug: meta.slug,
            excerpt: meta.excerpt,
            content: "",
            meta_description: meta.meta_description,
          },
        ],
        categories: meta.categories.map((cat) => cat.id),
      });

      handleSaveDrafts();
    }
  }, [isNew, handleSaveEntity, meta, locale, updateMutation, handleSaveDrafts]);

  const handlePublishAll = useCallback(async () => {
    try {
      await updateMutation.mutateAsync({
        ...meta,
        locale: locale,
        translations: [
          {
            locale: locale,
            title: meta.title,
            slug: meta.slug,
            excerpt: meta.excerpt,
            content: "",
            meta_description: meta.meta_description,
          },
        ],
        categories: meta.categories.map((cat) => cat.id),
      });

      await handlePublish();
      await publishEntityMutation.mutateAsync();
    } catch (error) {}
  }, [meta, locale, updateMutation, handlePublish, publishEntityMutation]);

  const wrappedHandleDiscardDraft = useCallback(async () => {
    setIsDiscardingState(true);
    try {
      await handleDiscard();
    } finally {
      setIsDiscardingState(false);
    }
  }, [handleDiscard]);

  useEffect(() => {
    setSaveHandler(handleSaveEntity);
  }, [handleSaveEntity, setSaveHandler]);

  useEffect(() => {
    setSaveDraftHandler(handleSaveAll);
  }, [handleSaveAll, setSaveDraftHandler]);

  useEffect(() => {
    setPublishHandler(handlePublishAll);
  }, [handlePublishAll, setPublishHandler]);

  useEffect(() => {
    setDiscardDraftHandler(wrappedHandleDiscardDraft);
  }, [wrappedHandleDiscardDraft, setDiscardDraftHandler]);

  useEffect(() => {
    const savingState =
      createMutation.isPending || updateMutation.isPending || isSaving;
    setIsSaving(savingState);
  }, [
    createMutation.isPending,
    updateMutation.isPending,
    isSaving,
    setIsSaving,
  ]);

  useEffect(() => {
    const publishingState = isPublishing || publishEntityMutation.isPending;
    setIsPublishing(publishingState);
  }, [isPublishing, publishEntityMutation.isPending, setIsPublishing]);

  useEffect(() => {
    setIsDiscarding(isDiscardingState);
  }, [isDiscardingState, setIsDiscarding]);

  const isValidId = id && (id === "new" || !isNaN(Number(id)));

  if (!isValidId) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Invalid {config.singularLabel} ID
          </h2>
          <p className="text-gray-500 mb-4">
            The {config.singular} ID in the URL is invalid or missing.
          </p>
          <Button onClick={() => navigate(config.routes.list)}>
            Go to {config.pluralLabel}
          </Button>
        </div>
      </div>
    );
  }

  const updateBlockOrder = useCallback(
    (blockId, sortOrder) => {
      updateBlock.mutate({
        blockId,
        data: { sort_order: sortOrder },
      });
    },
    [updateBlock]
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const activeBlock = blocks.find((b) => b.id === active.id);
    const overBlock = blocks.find((b) => b.id === over.id);

    if (activeBlock?.is_fixed || overBlock?.is_fixed) return;

    const regularBlocks = blocks.filter((b) => !b.is_fixed);
    const fixedBlocks = blocks.filter((b) => b.is_fixed);

    const oldIndex = regularBlocks.findIndex((block) => block.id === active.id);
    const newIndex = regularBlocks.findIndex((block) => block.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    const newRegularBlocks = arrayMove(regularBlocks, oldIndex, newIndex);

    newRegularBlocks.forEach((block, index) => {
      if (block.sort_order !== index) {
        updateBlockOrder(block.id, index);
      }
    });
  };

  useEffect(() => {
    setDraftStates({});
    setIsViewModeChanging(false);
  }, [id]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const blockId = urlParams.get("block");
    if (blockId) {
      setSelectedBlockId(Number(blockId));
    }
  }, [setSelectedBlockId]);

  const handleBlockSelect = (block) => {
    setSelectedBlockId(block.id);
    const url = new URL(window.location);
    url.searchParams.set("block", block.id);
    window.history.replaceState({}, "", url);
  };

  useEffect(() => {
    setIsViewModeChanging(true);
    if (viewMode === "live") {
      setDraftStates({});
    }

    const timer = setTimeout(() => {
      setIsViewModeChanging(false);
    }, 100);

    return () => clearTimeout(timer);
  }, [viewMode]);

  useEffect(() => {
    setIsLocaleChanging(true);
    setDraftStates({});

    const timer = setTimeout(() => {
      setIsLocaleChanging(false);
    }, 200);

    return () => clearTimeout(timer);
  }, [locale]);

  if (!_hasHydrated || !locale) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-500">Initializing...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 flex overflow-hidden">
        <div className="flex-1 flex flex-col">
          {!hasTranslation &&
            entity &&
            (entityType === "post" ? (
              <PostTranslationBanner
                post={entity}
                locale={locale}
                languageName={languageName}
                onTranslationCreated={() => {
                  queryClient.invalidateQueries({
                    queryKey: [config.api.queryKeys.single, id, locale],
                  });
                }}
              />
            ) : (
              <TranslationBanner
                page={entity}
                locale={locale}
                languageName={languageName}
                onTranslationCreated={() => {
                  queryClient.invalidateQueries({
                    queryKey: [config.api.queryKeys.single, id, locale],
                  });
                }}
              />
            ))}

          {(isNew || viewMode === "draft") && (
            <div className="flex-1 overflow-auto p-6">
              <div className="max-w-4xl mx-auto">
                {!isNew && blocksLoading ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="text-gray-500 mt-2">Loading blocks...</p>
                  </div>
                ) : blocks.length === 0 ? (
                  <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                    <Plus className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      No blocks yet
                    </h3>
                    <p className="text-gray-500 mb-4">
                      Add your first block using the sidebar
                    </p>
                  </div>
                ) : (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={blocks.filter((b) => !b.is_fixed).map((block) => block.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="space-y-4">
                        {blocks.map((block, index) => (
                          <SortableBlock
                            key={block.id}
                            block={block}
                            index={index}
                            isSelected={selectedBlockId === block.id}
                            onSelect={handleBlockSelect}
                            viewMode={viewMode}
                            onFieldChange={handleFieldChange}
                            draftStates={draftStates}
                            locale={locale}
                            readOnly={isNew ? false : !hasTranslation}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                )}
              </div>
            </div>
          )}
          {!isNew && viewMode === "live" && (
            <div className="flex-1 overflow-auto p-6">
              <div className="max-w-4xl mx-auto">
                {blocksLoading ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="text-gray-500 mt-2">Loading blocks...</p>
                  </div>
                ) : blocks.length === 0 ? (
                  <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                    <Eye className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      No published content
                    </h3>
                    <p className="text-gray-500 mb-4">
                      This {config.singular} has no published blocks yet
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {blocks.map((block, index) => (
                      <SortableBlock
                        key={block.id}
                        block={block}
                        index={index}
                        isSelected={selectedBlockId === block.id}
                        onSelect={handleBlockSelect}
                        viewMode={viewMode}
                        onFieldChange={handleFieldChange}
                        draftStates={draftStates}
                        locale={locale}
                        readOnly={!hasTranslation}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <ContentMetaSidebar
          entity={entity}
          entityType={entityType}
          isNew={isNew}
          locale={locale}
        />
      </div>
    </div>
  );
}
